/**
 * HomepageModal - Homepage Oficial de DamyxIA
 * Documentación Completa y Final
 */

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { X, Home, Sparkles, Layout, Image, FileText, History, Lightbulb, ChevronDown, ChevronUp, Eye, Palette, Copy, Upload, CheckCircle, FolderOpen, Crown, ArrowRight, Info, Gift, HelpCircle, Github } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import LyonAlcancePanel from './LyonAlcancePanel';
import LyonHelpInfoModal from './LyonHelpInfoModal';
import LyonGenieIcon from './LyonGenieIcon';
import PlanesReglasModal from './PlanesReglasModal';
import CapabilitiesGuide from './CapabilitiesGuide';

const translations = {
  es: {
    // Header
    title: 'DamyxIA',
    subtitle: 'Tu espacio de trabajo inteligente, visual y organizado',
    
    // Quick Summary
    quickSummaryTitle: 'Resumen Rápido',
    quickSummary: 'DamyxIA es un entorno de trabajo donde podés conversar, crear, organizar y transformar ideas.',
    quickSummaryPoints: [
      'El chat es tu diálogo.',
      'El Área de Trabajo es tu proyecto.',
      'Podés analizar, editar y replicar imágenes, crear notas, documentos y tareas, y mantener versiones claras de todo lo que hacés.'
    ],
    
    // Natural Interaction Section
    naturalInteractionTitle: 'Interacción Natural para Mejores Respuestas',
    naturalInteractionIntro: 'DamyxIA responde mejor cuando la conversación comienza con una frase corta o un saludo. Esto ayuda al sistema a entrar en "modo conversacional" y mejora la precisión de las respuestas.',
    naturalInteractionRecommendation: 'Antes de dar una instrucción técnica, iniciá con frases breves como:',
    naturalInteractionExamples: [
      '"Hola, una cosa…"',
      '"Ok, mira…"',
      '"DamyxIA, escucha…"',
      '"Bro, necesito esto…"'
    ],
    naturalInteractionExplanation: 'Un saludo o frase corta le indica a DamyxIA que estás iniciando una conversación humana. Esto mejora la precisión, evita errores y hace que la IA entienda mejor tu intención.',
    naturalInteractionSummary: 'Un pequeño saludo mejora la comunicación y hace que DamyxIA responda de forma más clara y enfocada.',
    
    // Section 1: Welcome — 🟥 P24.3 · Reemplazado por Guía de Capacidades
    // completa (owner-approved). El highlight corto se eliminó a favor
    // de un layout de tarjetas navegable con scroll interno.
    section1Title: 'Bienvenido a DamyxIA',
    section1Content: 'DamyxIA combina conversación inteligente con herramientas reales de trabajo. Podés:',
    section1Highlight: 'pensar, crear, analizar, transformar y organizar todo en un mismo lugar, con tu proyecto siempre visible en el Workspace.',

    // 🟪 GUÍA DE CAPACIDADES v1.0 — reemplaza al recuadro-highlight simple.
    guide: {
      badge: 'v1.0 · Guía Oficial',
      title: '¿Qué puedo crear con DamyxIA?',
      subtitle: 'Guía completa para sacar el máximo provecho de tu asistente de desarrollo. Descubre qué funciona en cada modo y cómo pedir lo que necesitas.',
      ctaModes: 'Explorar modos',
      ctaFormulas: 'Ver fórmulas',

      modesTitle: 'Los 2 modos de trabajo',
      modesSubtitle: 'Elige cómo quieres que DamyxIA trabaje para ti',
      modeChatTitle: 'Modo Chat (sin Builder)',
      modeChatTag: 'Solo Preview',
      modeChatDesc: 'DamyxIA responde en el chat. El preview del Área de Trabajo ejecuta el código en tiempo real. Perfecto para prototipos rápidos y herramientas simples.',
      modeBuilderTitle: 'Modo Builder (toggle ON)',
      modeBuilderTag: 'Proyecto Descargable',
      modeBuilderDesc: 'DamyxIA escribe archivos reales en tu proyecto. Descárgalo y córrelo sin límites en tu dispositivo o servidor. Sin restricciones de sandbox.',

      chatCapsTitle: 'Modo Chat — Qué funciona en el Preview',
      chatCapsTag: 'Sin Builder',
      chatCapsSubtitle: 'Capacidades y limitaciones del entorno sandboxed del preview',
      chatWorksTitle: 'Esto sí funciona',
      chatWorks: [
        ['Calculadoras', 'Financiera, IMC, propinas, conversores'],
        ['Formularios interactivos', 'Contacto, registro, encuestas con validación'],
        ['Listas y checklists', 'To-do, tareas, rankings editables'],
        ['Timers y contadores', 'Cronómetro, Pomodoro, cuenta regresiva'],
        ['Juegos simples', 'Memoria, ahorcado, trivia, adivina el número'],
        ['Editores de texto', 'Notas, cuadros editables, diarios'],
        ['Generadores', 'Contraseñas, nombres, IDs aleatorios'],
        ['Dashboards con datos fijos', 'Tablas, métricas, reportes estáticos'],
        ['Landing pages', 'Presentación con diseño y animaciones CSS'],
        ['Quiz / Trivia', 'Preguntas y respuestas con puntaje'],
        ['Canvas / Dibujo', 'Pizarra simple, paint básico'],
        ['Color pickers', 'Paletas, gradientes, conversores'],
      ],
      chatFailsTitle: 'Esto NO funciona en el preview',
      chatFails: [
        ['Apps móviles', 'React Native no corre en browser', 'Error: StyleSheet.create is not a function'],
        ['Guardar datos permanentes', 'iframe sandboxed bloquea localStorage', 'Error: localStorage sandboxed'],
        ['Librerías externas', 'npm packages bloqueados', 'Error: X is not defined'],
        ['Drag & drop con librería', 'react-beautiful-dnd bloqueado', 'Error: DragDropContext not defined'],
        ['Enviar correos', 'Sin acceso a SMTP', 'No envía nada'],
        ['Generar PDFs reales', 'jsPDF bloqueado', 'Error: jsPDF is not defined'],
        ['Llamadas a APIs externas', 'CORS bloqueado en sandbox', 'Error: fetch failed'],
      ],

      builderTitle: 'Modo Builder — Todo lo que puedes descargar',
      builderTag: 'Builder ON',
      builderSubtitle: 'Sin límites. Descarga el proyecto y ejecútalo donde quieras.',
      builderWarning: 'El preview puede mostrar errores — eso es normal. Lo importante es descargar el proyecto y ejecutarlo en tu dispositivo o servidor.',
      builderWebTitle: 'Apps Web',
      builderWeb: [
        'Apps multi-pantalla con React Router',
        'CRUD completo con localStorage real',
        'Dashboards con Chart.js / Recharts',
        'Sistemas de login y autenticación',
        'Blogs y CMS editables',
        'E-commerce frontend completo',
        'Herramientas SaaS (CRM, facturación)',
        'Portfolios profesionales animados',
        'Chats UI con canales y hilos',
        'Calendarios con drag & drop',
      ],
      builderMobileTitle: 'Apps Móviles',
      builderMobile: [
        'Scanner de documentos (foto→PDF→email)',
        'Apps con cámara y filtros',
        'GPS y mapas interactivos',
        'Notificaciones push',
        'Apps de salud y bienestar',
        'Tiendas móviles con Stripe',
        'Redes sociales completas',
        'Apps de productividad',
      ],

      formulasTitle: 'Las fórmulas que siempre funcionan',
      formulasSubtitle: 'Copia y pega estos prompts para obtener los mejores resultados',
      formulaChatTitle: 'Para apps web en Chat',
      formulaChatCode: '"Crea una [app/herramienta] web usando\nsolo React puro + useState + CSS.\nSin librerías externas.\nDiseño oscuro con acento #7C3AED."',
      formulaBuilderWebTitle: 'Para apps web con Builder',
      formulaBuilderWebCode: '"Con el Builder activo, crea una app\nweb React con [describe lo que quieres].\nUsa React Router para navegación.\nGuarda datos en localStorage."',
      formulaBuilderMobileTitle: 'Para apps móviles con Builder',
      formulaBuilderMobileCode: '"Con el Builder activo, crea una app\nExpo/React Native con [descripción].\nConfigura el package.json completo.\nLista para abrir en Expo Go."',

      stepsTitle: 'Cómo activar el Builder',
      stepsSubtitle: '5 pasos simples para desbloquear todo el potencial',
      steps: [
        ['Abre el Área de Trabajo Interna', 'Desde la barra lateral de DamyxIA'],
        ['Selecciona o crea un proyecto', 'Puedes usar un proyecto existente o iniciar uno nuevo'],
        ['Busca el toggle en la barra del chat', 'Está en la esquina derecha de la barra de entrada'],
        ['Cambia de 🔒 Builder OFF → 🔓 Builder ON', 'Un solo clic para activar el modo completo'],
        ['¡Listo! DamyxIA ahora escribe archivos reales', 'Descarga tu proyecto cuando lo necesites'],
      ],
      toggleOff: 'Builder OFF',
      toggleOn: 'Builder ON',
    },
    
    // Plans Block
    plansBlockTitle: 'Planes y Beneficios',
    plansBlockDesc: 'Conocé los planes disponibles, límites de uso y el programa de referidos.',
    plansBlockCta: 'Ver planes',
    
    // Rewards System Block
    rewardsBlockTitle: 'Sistema de Recompensas',
    rewardsBlockDesc: 'Reglas del Plan Gratis, recompensas Premium por referidos y prohibiciones.',
    rewardsBlockCta: 'Ver reglas',

    // 🟥 GitHub Integration Block (2026-07-06) — entre Recompensas y Panel del Creador.
    githubBlockTitle: 'Integración de Proyectos a GitHub',
    githubBlockDesc: 'Cómo convertir un Trabajo del Workspace en una App del Builder exportable a GitHub.',
    githubBlockCta: 'Ver guía',

    // Creator Panel Block
    creatorBlockTitle: 'Panel del Creador',
    creatorBlockDesc: 'Métricas, usuarios, estado del sistema y finanzas del creador.',
    creatorBlockCta: 'Acceder',

    // About DamyxIA Block (abre /capabilities en nueva ventana)
    aboutBlockTitle: 'Que Saber sobre DamyxIA',
    aboutBlockDesc: 'Conocé en profundidad todo lo que DamyxIA puede hacer: cómo funciona, qué genera, modo alternativo, plantillas IA y más.',
    aboutBlockCta: 'Abrir',
    
    // Section 2: Workspace
    section2Title: 'El Área de Trabajo (Workspace)',
    section2Intro: 'El Workspace es donde vive tu proyecto.',
    section2WhatTitle: 'Qué hace el Workspace:',
    section2WhatItems: ['Muestra tu app o interfaz en tiempo real', 'Cada cambio se aplica como un parche incremental, no como una regeneración completa', 'El iframe no se recarga, no parpadea y no pierde estado'],
    section2HowTitle: 'Tu Workspace es tu proyecto vivo.',
    section2HowItems: [
      'El chat es cómo lo vas transformando.',
      'Los cambios se aplican de forma instantánea sin destruir lo que ya existe.',
      'Todo queda organizado para que puedas trabajar con claridad.'
    ],
    section2Highlight: 'Tu Workspace es tu proyecto vivo. El chat es cómo lo vas transformando.',
    
    // Section 3: Vision Replication
    section3Title: 'Vision_Replication — Trabajo Avanzado con Imágenes',
    section3Intro: 'Vision_Replication es el entorno visual avanzado de DamyxIA.',
    
    section3AnalysisTitle: '3.1. Ver, Comparar y Analizar',
    section3AnalysisIntro: 'Con Vision_Replication podés:',
    section3AnalysisCapabilities: ['ver y comparar imágenes', 'analizar composición y colores', 'integrarlo en tu flujo de trabajo'],
    section3AnalysisMode: 'Es la capa visual que acompaña tus ideas:',
    section3AnalysisModePoints: ['Analiza sin generar nuevas imágenes', 'Solo publica cuando vos lo pedís', 'Complementa el chat con análisis visual'],
    section3AnalysisExamples: ['"¿Qué ves en esta imagen?"', '"Analizá los colores de esta foto."'],
    
    section3ReplicationTitle: '3.2. Replicación (Modo Trabajo)',
    section3ReplicationIntro: 'Podés pedir:',
    section3ReplicationExamples: ['"Hacé una copia igual."', '"Hacé una versión estilizada."', '"Generá una variación."'],
    section3ReplicationResult: 'DamyxIA crea una nueva imagen y la publica en el Workspace.',
    
    section3EditTitle: '3.3. Edición (Modo Trabajo)',
    section3EditIntro: 'Podés pedir:',
    section3EditExamples: ['"Quitá el fondo."', '"Cambiá los colores."', '"Hacela estilo acuarela."', '"Mejorá la iluminación."'],
    section3EditResult: 'DamyxIA genera la versión editada y la publica en el Workspace.',
    
    section3ModesTitle: '3.4. Activación de modos',
    section3AnalysisModeTitle: 'Modo Análisis (por defecto):',
    section3AnalysisModeDesc: ['describe, analiza, interpreta', 'NO publica', 'NO genera imágenes'],
    section3WorkModeTitle: 'Modo Trabajo (cuando vos lo pedís):',
    section3WorkModeDesc: ['publica en el Workspace', 'edita', 'replica', 'transforma'],
    section3Keywords: 'Palabras clave que activan Modo Trabajo:',
    section3KeywordsList: '"muéstrala", "mostrar", "área de trabajo", "workspace", "replica", "copia", "duplica", "edita", "modifica", "quita", "cambia", "fondo", "colores".',
    
    // Section 4: Upload Images
    section4Title: 'Subir Imágenes desde el Chat',
    section4Intro: 'Podés subir imágenes directamente desde el chat para analizarlas, describirlas, transformarlas o usarlas como base para nuevas versiones.',
    section4HowTitle: 'Cómo funciona:',
    section4HowItems: [
      'Hay un botón con ícono de imagen al lado del input.',
      'Al tocarlo, se abre un selector de archivos.',
      'Podés elegir cualquier imagen de tu dispositivo.',
      'DamyxIA recibe la imagen y entra automáticamente en Modo Análisis.',
      'Si agregás instrucciones ("muéstrala", "quitá el fondo", etc.), entra en Modo Trabajo.'
    ],
    section4BehaviorTitle: 'DamyxIA entiende la imagen como parte de la conversación:',
    section4BehaviorItems: [
      'Imagen sola → Modo Análisis',
      'Imagen + instrucción → Modo Trabajo',
      'Imagen + edición → GPT Image 1',
      'Imagen + "muéstrala" → Workspace'
    ],
    section4TestsTitle: 'Pruebas confirmadas:',
    section4TestsItems: [
      'Subir imagen sin texto → Análisis',
      'Subir imagen + "muéstrala" → Trabajo + Workspace',
      'Subir imagen + "quitá el fondo" → Edición + Workspace',
      'Subir imagen vacía → Análisis por defecto'
    ],
    
    // Section 4.7: Cómo funciona la Papelera (Spec oficial)
    sectionTrashTitle: 'Cómo funciona la Papelera',
    sectionTrashSubtitle: 'Todo lo que borres queda 30 días disponible para recuperar',
    sectionTrashSteps: [
      'Cuando generas una imagen: se guarda en tu galería.',
      'Cuando borras una imagen: va a la Papelera (Free y Premium).',
      'Cuando borras una conversación: sus imágenes también van a la Papelera.',
      'Free: no puede ver el contenido. Puede recuperar pagando $0.50 por item. Puede activar la semana ilimitada por $3. Retención 30 días.',
      'Premium: acceso completo. Recuperación ilimitada. Retención 30 días.',
      'Stripe: pagos seguros con Stripe Checkout. Recuperación individual ($0.50) o semanal ($3).'
    ],

    // Section 4.8: Límites de texto en el cuadro de diálogo
    sectionTextLimitsTitle: 'Límites de texto en el cuadro de diálogo',
    sectionTextLimitsIntro: 'DamyxIA permite enviar textos largos sin romperse. El límite real por mensaje es:',
    sectionTextLimitsHighlight: '25.000 palabras (≈120.000 caracteres).',
    sectionTextLimitsErrorIntro: 'Si pegás un texto más grande, aparecerá automáticamente el mensaje:',
    sectionTextLimitsErrorMsg: '“Límite superado — máximo permitido: 25.000 palabras por mensaje.”',
    sectionTextLimitsTotal: 'El límite total de la conversación es de 80.000–100.000 palabras. Si se supera, DamyxIA resume partes viejas para mantener estabilidad.',
    sectionTextLimitsTip: 'Para documentos extremadamente grandes, se recomienda dividirlos en bloques de 10.000–15.000 palabras para mantener velocidad y precisión.',
    
    // Section 5: Create and Organize
    section5Title: 'Crear y Organizar Contenido desde el Chat',
    section5CreateTitle: '5.1. Crear elementos',
    section5CreateIntro: 'Desde el chat podés:',
    section5CreateExamples: ['"Creá una nota con este texto."', '"Generá un documento con este resumen."', '"Registrá esta tarea."', '"Vinculá esta idea con el Workspace."'],
    section5CreateResult: 'Todo lo que pensás puede convertirse en algo visible y organizado.',
    section5UpdateTitle: '5.2. Actualizar elementos',
    section5UpdateIntro: 'Podés decir:',
    section5UpdateExamples: ['"Actualizá la nota del proyecto."', '"Agregá esta información al documento."', '"Reemplazá la imagen anterior por esta nueva versión."'],
    section5OrgTitle: '5.3. Organización',
    section5OrgIntro: 'El Workspace te permite:',
    section5OrgItems: ['ver todo lo que estás creando', 'mantener orden', 'trabajar por bloques', 'tener siempre a mano lo importante'],
    
    // Section 6: History
    section6Title: 'Historial, Versiones y Control',
    section6Content: 'DamyxIA mantiene:',
    section6Points: [
      'historial de conversaciones',
      'cambios aplicados',
      'versiones del proyecto'
    ],
    section6Highlight: 'Podés revisar qué se hizo, cuándo y por qué.',
    
    // Section 7: Best Practices
    section7Title: 'Buenas Prácticas',
    section7Items: [
      'Para el chat: empezá con un saludo breve.',
      'Explicá qué querés lograr.',
      'Pedí cambios específicos.',
      'Para el Workspace: evitá pedir "reemplaza todo".',
      'Preferí instrucciones como "cambia el color de este botón".',
      'Para imágenes: usá máscaras precisas.',
      'Describí con claridad, evitá cambios gigantes en una sola pasada.'
    ],
    
    // Section 8: Final Message
    section8Title: 'Mensaje Final',
    section8Lines: [
      'DamyxIA es tu espacio de trabajo inteligente.',
      'Un lugar donde podés pensar, crear, analizar, transformar y organizar todo lo que imaginás.',
      'Tu chat es la conversación.',
      'Tu Workspace es tu proyecto.',
      'Vision_Replication es tu herramienta visual avanzada.'
    ],
    section8Final: 'Todo lo que necesitás para construir está acá.',
    
    // 🟦 CUADRO NEGRO bug-idioma — Panel "DamyxIA / Lyon — Funciones".
    funcionesPanel: {
      title: 'DamyxIA / Lyon — Funciones',
      whatIsDamyxiaTitle: '🧠 ¿Qué es DamyxIA?',
      whatIsDamyxiaBody: 'DamyxIA es la plataforma completa donde vivís, trabajás y creás. Se encarga de tus Apps, el Builder, el Marketplace, el Workspace, la publicación, el guardado real y tu cuenta.',
      whatIsLyonTitle: '🤖 ¿Qué es Lyon?',
      whatIsLyonBody: 'Lyon es el asistente inteligente dentro de DamyxIA. Te guía, te explica, te ayuda y automatiza tareas dentro del Genio. No guarda, no publica, no maneja tu cuenta.',
      howTogetherTitle: '🔗 ¿Cómo trabajan juntos?',
      howTogetherLines: [
        'Lyon te guía. DamyxIA ejecuta.',
        'Lyon abre módulos. DamyxIA los carga.',
        'Lyon sugiere. DamyxIA guarda.',
      ],
      whereWorkTitle: '🛠️ ¿Dónde hago mi trabajo?',
      whereWorkItems: [
        'Crear App → DamyxIA',
        'Editar App → DamyxIA (Builder)',
        'Publicar → DamyxIA',
        'Importar plantilla → DamyxIA (guiado por Lyon)',
        'Resolver dudas → Lyon',
        'Generar contenido → Lyon',
      ],
      summaryTitle: 'Resumen:',
      summaryLines: [
        'Lyon es tu copiloto.',
        'DamyxIA es tu plataforma.',
      ],
      scrollTop: 'Volver al inicio',
      closeAria: 'Cerrar',
    },

    close: 'Cerrar'
  },
  en: {
    // Header
    title: 'DamyxIA',
    subtitle: 'Your intelligent, visual, and organized workspace',
    
    // Quick Summary
    quickSummaryTitle: 'Quick Summary',
    quickSummary: 'DamyxIA is a workspace where you can chat, create, organize, and transform ideas.',
    quickSummaryPoints: [
      'The chat is your dialogue.',
      'The Workspace is your project.',
      'You can analyze, edit, and replicate images, create notes, documents, and tasks, and keep clear versions of everything you build.'
    ],
    
    // Natural Interaction Section
    naturalInteractionTitle: 'Natural Interaction for Better Responses',
    naturalInteractionIntro: 'DamyxIA responds better when the conversation starts with a short phrase or greeting. This helps the system enter "conversational mode" and improves response accuracy.',
    naturalInteractionRecommendation: 'Before giving a technical instruction, start with brief phrases like:',
    naturalInteractionExamples: [
      '"Hello, one thing…"',
      '"Ok, look…"',
      '"DamyxIA, listen…"',
      '"Hey, I need this…"'
    ],
    naturalInteractionExplanation: 'A greeting or short phrase tells DamyxIA that you are starting a human conversation. This improves accuracy, avoids errors, and helps the AI better understand your intent.',
    naturalInteractionSummary: 'A small greeting improves communication and makes DamyxIA respond more clearly and focused.',
    
    // Section 1: Welcome
    section1Title: 'Welcome to DamyxIA',
    section1Content: 'DamyxIA combines intelligent conversation with real working tools. You can:',
    section1Highlight: 'think, create, analyze, transform, and organize everything in one place, with your project always visible in the Workspace.',

    // 🟪 CAPABILITIES GUIDE v1.0 — English mirror.
    guide: {
      badge: 'v1.0 · Official Guide',
      title: 'What can I build with DamyxIA?',
      subtitle: 'Complete guide to get the most out of your dev assistant. Discover what works in each mode and how to ask for what you need.',
      ctaModes: 'Explore modes',
      ctaFormulas: 'View formulas',

      modesTitle: 'The 2 work modes',
      modesSubtitle: 'Choose how you want DamyxIA to work for you',
      modeChatTitle: 'Chat Mode (no Builder)',
      modeChatTag: 'Preview only',
      modeChatDesc: 'DamyxIA responds in the chat. The Workspace preview runs the code in real time. Perfect for quick prototypes and simple tools.',
      modeBuilderTitle: 'Builder Mode (toggle ON)',
      modeBuilderTag: 'Downloadable Project',
      modeBuilderDesc: 'DamyxIA writes real files to your project. Download it and run it without limits on your device or server. No sandbox restrictions.',

      chatCapsTitle: 'Chat Mode — What works in the Preview',
      chatCapsTag: 'No Builder',
      chatCapsSubtitle: 'Capabilities and limits of the sandboxed preview environment',
      chatWorksTitle: 'This does work',
      chatWorks: [
        ['Calculators', 'Financial, BMI, tips, converters'],
        ['Interactive forms', 'Contact, signup, surveys with validation'],
        ['Lists and checklists', 'To-do, tasks, editable rankings'],
        ['Timers and counters', 'Stopwatch, Pomodoro, countdown'],
        ['Simple games', 'Memory, hangman, trivia, guess the number'],
        ['Text editors', 'Notes, editable boxes, journals'],
        ['Generators', 'Passwords, names, random IDs'],
        ['Dashboards with fixed data', 'Tables, metrics, static reports'],
        ['Landing pages', 'Design with CSS animations'],
        ['Quiz / Trivia', 'Q&A with scoring'],
        ['Canvas / Drawing', 'Simple whiteboard, basic paint'],
        ['Color pickers', 'Palettes, gradients, converters'],
      ],
      chatFailsTitle: 'This does NOT work in the preview',
      chatFails: [
        ['Mobile apps', 'React Native does not run in browser', 'Error: StyleSheet.create is not a function'],
        ['Saving permanent data', 'Sandboxed iframe blocks localStorage', 'Error: localStorage sandboxed'],
        ['External libraries', 'npm packages blocked', 'Error: X is not defined'],
        ['Drag & drop libraries', 'react-beautiful-dnd blocked', 'Error: DragDropContext not defined'],
        ['Sending emails', 'No SMTP access', 'Sends nothing'],
        ['Generating real PDFs', 'jsPDF blocked', 'Error: jsPDF is not defined'],
        ['External API calls', 'CORS blocked in sandbox', 'Error: fetch failed'],
      ],

      builderTitle: 'Builder Mode — Everything you can download',
      builderTag: 'Builder ON',
      builderSubtitle: 'No limits. Download the project and run it wherever you want.',
      builderWarning: 'The preview may show errors — that\'s normal. What matters is downloading the project and running it on your device or server.',
      builderWebTitle: 'Web Apps',
      builderWeb: [
        'Multi-screen apps with React Router',
        'Full CRUD with real localStorage',
        'Dashboards with Chart.js / Recharts',
        'Login and authentication systems',
        'Editable blogs and CMS',
        'Complete e-commerce frontend',
        'SaaS tools (CRM, invoicing)',
        'Animated professional portfolios',
        'Chat UIs with channels and threads',
        'Calendars with drag & drop',
      ],
      builderMobileTitle: 'Mobile Apps',
      builderMobile: [
        'Document scanner (photo→PDF→email)',
        'Camera and filter apps',
        'GPS and interactive maps',
        'Push notifications',
        'Health and wellness apps',
        'Mobile stores with Stripe',
        'Complete social networks',
        'Productivity apps',
      ],

      formulasTitle: 'Formulas that always work',
      formulasSubtitle: 'Copy and paste these prompts for the best results',
      formulaChatTitle: 'For web apps in Chat',
      formulaChatCode: '"Create a [app/tool] web using\nonly pure React + useState + CSS.\nNo external libraries.\nDark design with #7C3AED accent."',
      formulaBuilderWebTitle: 'For web apps with Builder',
      formulaBuilderWebCode: '"With Builder active, create a\nReact web app that [describe what you want].\nUse React Router for navigation.\nStore data in localStorage."',
      formulaBuilderMobileTitle: 'For mobile apps with Builder',
      formulaBuilderMobileCode: '"With Builder active, create an\nExpo/React Native app with [description].\nSet up the complete package.json.\nReady to open in Expo Go."',

      stepsTitle: 'How to activate Builder',
      stepsSubtitle: '5 simple steps to unlock the full potential',
      steps: [
        ['Open the Internal Workspace', 'From the DamyxIA sidebar'],
        ['Select or create a project', 'Use an existing project or start a new one'],
        ['Find the toggle in the chat bar', 'It is on the right edge of the input bar'],
        ['Switch from 🔒 Builder OFF → 🔓 Builder ON', 'One click to activate full mode'],
        ['Done! DamyxIA now writes real files', 'Download your project whenever you need'],
      ],
      toggleOff: 'Builder OFF',
      toggleOn: 'Builder ON',
    },
    
    // Plans Block
    plansBlockTitle: 'Plans and Benefits',
    plansBlockDesc: 'Discover available plans, usage limits and the referral program.',
    plansBlockCta: 'View plans',
    
    // Rewards System Block
    rewardsBlockTitle: 'Rewards System',
    rewardsBlockDesc: 'Free Plan rules, Premium rewards by referrals and prohibitions.',
    rewardsBlockCta: 'View rules',

    // 🟥 GitHub Integration Block (2026-07-06) — between Rewards and Creator Panel.
    githubBlockTitle: 'GitHub Project Integration',
    githubBlockDesc: 'How to convert a Workspace Job into a Builder App exportable to GitHub.',
    githubBlockCta: 'View guide',

    // Creator Panel Block
    creatorBlockTitle: 'Creator Panel',
    creatorBlockDesc: 'Metrics, users, system status and creator finances.',
    creatorBlockCta: 'Access',

    // About DamyxIA Block (opens /home in new window)
    aboutBlockTitle: 'What to know about DamyxIA',
    aboutBlockDesc: 'Discover in depth everything DamyxIA can do: how it works, what it generates, alternative mode, AI templates and more.',
    aboutBlockCta: 'Open',
    
    // Section 2: Workspace
    section2Title: 'The Workspace',
    section2Intro: 'The Workspace is where your project lives.',
    section2WhatTitle: 'What the Workspace does:',
    section2WhatItems: ['Shows your app or interface in real time', 'Each change is applied as an incremental patch, not a complete regeneration', 'The iframe does not reload, does not flash, and does not lose state'],
    section2HowTitle: 'Your Workspace is your living project.',
    section2HowItems: [
      'The chat is how you transform it.',
      'Changes are applied instantly without destroying what already exists.',
      'Everything stays organized so you can work clearly.'
    ],
    section2Highlight: 'Your Workspace is your living project. The chat is how you transform it.',
    
    // Section 3: Vision Replication
    section3Title: 'Vision_Replication — Advanced Image Work',
    section3Intro: 'Vision_Replication is DamyxIA\'s advanced visual environment.',
    
    section3AnalysisTitle: '3.1. View, Compare and Analyze',
    section3AnalysisIntro: 'With Vision_Replication you can:',
    section3AnalysisCapabilities: ['view and compare images', 'analyze composition and colors', 'integrate it into your workflow'],
    section3AnalysisMode: 'It is the visual layer that accompanies your ideas:',
    section3AnalysisModePoints: ['Analyzes without generating new images', 'Only publishes when you ask', 'Complements the chat with visual analysis'],
    section3AnalysisExamples: ['"What do you see in this image?"', '"Analyze the colors of this photo."'],
    
    section3ReplicationTitle: '3.2. Replication (Work Mode)',
    section3ReplicationIntro: 'You can request:',
    section3ReplicationExamples: ['"Make an identical copy."', '"Create a stylized version."', '"Generate a variation."'],
    section3ReplicationResult: 'DamyxIA creates a new image and publishes it to the Workspace.',
    
    section3EditTitle: '3.3. Editing (Work Mode)',
    section3EditIntro: 'You can request:',
    section3EditExamples: ['"Remove the background."', '"Change the colors."', '"Make it watercolor style."', '"Improve the lighting."'],
    section3EditResult: 'DamyxIA generates the edited version and publishes it to the Workspace.',
    
    section3ModesTitle: '3.4. Mode Activation',
    section3AnalysisModeTitle: 'Analysis Mode (default):',
    section3AnalysisModeDesc: ['describes, analyzes, interprets', 'does NOT publish', 'does NOT generate images'],
    section3WorkModeTitle: 'Work Mode (when you request it):',
    section3WorkModeDesc: ['publishes to the Workspace', 'edits', 'replicates', 'transforms'],
    section3Keywords: 'Keywords that activate Work Mode:',
    section3KeywordsList: '"show it", "show", "workspace", "area", "replicate", "copy", "duplicate", "edit", "modify", "remove", "change", "background", "colors".',
    
    // Section 4: Upload Images
    section4Title: 'Uploading Images from the Chat',
    section4Intro: 'You can upload images directly from the chat to analyze, describe, transform, or use them as a base for new versions.',
    section4HowTitle: 'How it works:',
    section4HowItems: [
      'There is an image icon button next to the chat input.',
      'Tapping it opens a file selector.',
      'You can choose any image from your device.',
      'DamyxIA receives the image and automatically enters Analysis Mode.',
      'If you add instructions ("show it", "remove the background", etc.), it switches to Work Mode.'
    ],
    section4BehaviorTitle: 'DamyxIA understands the image as part of the conversation:',
    section4BehaviorItems: [
      'Image alone → Analysis Mode',
      'Image + instruction → Work Mode',
      'Image + editing → GPT Image 1',
      'Image + "show it" → Workspace'
    ],
    section4TestsTitle: 'Confirmed tests:',
    section4TestsItems: [
      'Upload image without text → Analysis',
      'Upload image + "show it" → Work Mode + Workspace',
      'Upload image + "remove the background" → Editing + Workspace',
      'Upload empty image → Analysis Mode by default'
    ],
    
    // Section 4.7: How the Trash works (Official spec)
    sectionTrashTitle: 'How the Trash works',
    sectionTrashSubtitle: 'Everything you delete stays available to recover for 30 days',
    sectionTrashSteps: [
      'When you generate an image: it is saved to your gallery.',
      'When you delete an image: it goes to the Trash (Free and Premium).',
      'When you delete a conversation: its images also go to the Trash.',
      'Free: cannot see the content. Can recover by paying $0.50 per item. Can activate the weekly unlimited for $3. 30-day retention.',
      'Premium: full access. Unlimited recovery. 30-day retention.',
      'Stripe: secure payments with Stripe Checkout. Individual ($0.50) or weekly ($3) recovery.'
    ],

    // Section 4.8: Text limits in the chat box
    sectionTextLimitsTitle: 'Text limits in the chat box',
    sectionTextLimitsIntro: 'DamyxIA lets you send long texts without breaking. The real per-message limit is:',
    sectionTextLimitsHighlight: '25,000 words (≈120,000 characters).',
    sectionTextLimitsErrorIntro: 'If you paste a larger text, this message will appear automatically:',
    sectionTextLimitsErrorMsg: '“Limit exceeded — maximum allowed: 25,000 words per message.”',
    sectionTextLimitsTotal: 'The total conversation limit is 80,000–100,000 words. If exceeded, DamyxIA summarizes older parts to maintain stability.',
    sectionTextLimitsTip: 'For extremely large documents, split them into 10,000–15,000 word blocks to keep speed and accuracy.',
    
    // Section 5: Create and Organize
    section5Title: 'Creating and Organizing Content from the Chat',
    section5CreateTitle: '5.1. Creating elements',
    section5CreateIntro: 'From the chat you can:',
    section5CreateExamples: ['"Create a note with this text."', '"Generate a document with this summary."', '"Register this task."', '"Link this idea with the Workspace."'],
    section5CreateResult: 'Everything you think can become something visible and organized.',
    section5UpdateTitle: '5.2. Updating elements',
    section5UpdateIntro: 'You can say:',
    section5UpdateExamples: ['"Update the project note."', '"Add this information to the document."', '"Replace the previous image with this new version."'],
    section5OrgTitle: '5.3. Organization',
    section5OrgIntro: 'The Workspace allows you to:',
    section5OrgItems: ['see everything you are creating', 'keep things organized', 'work in blocks', 'always have important items at hand'],
    
    // Section 6: History
    section6Title: 'History, Versions, and Control',
    section6Content: 'DamyxIA maintains:',
    section6Points: [
      'conversation history',
      'applied changes',
      'project versions'
    ],
    section6Highlight: 'You can review what was done, when and why.',
    
    // Section 7: Best Practices
    section7Title: 'Best Practices',
    section7Items: [
      'For the chat: start with a brief greeting.',
      'Explain what you want to achieve.',
      'Ask for specific changes.',
      'For the Workspace: avoid asking "replace everything".',
      'Prefer instructions like "change the color of this button".',
      'For images: use precise masks.',
      'Describe clearly, avoid giant changes in a single pass.'
    ],
    
    // Section 8: Final Message
    section8Title: 'Final Message',
    section8Lines: [
      'DamyxIA is your intelligent workspace.',
      'A place where you can think, create, analyze, transform, and organize everything you imagine.',
      'Your chat is the conversation.',
      'Your Workspace is your project.',
      'Vision_Replication is your advanced visual tool.'
    ],
    section8Final: 'Everything you need to build is here.',
    
    // 🟦 CUADRO NEGRO bug-idioma — "DamyxIA / Lyon — Features" panel.
    funcionesPanel: {
      title: 'DamyxIA / Lyon — Features',
      whatIsDamyxiaTitle: '🧠 What is DamyxIA?',
      whatIsDamyxiaBody: 'DamyxIA is the full platform where you live, work and create. It handles your Apps, the Builder, the Marketplace, the Workspace, publishing, real saving and your account.',
      whatIsLyonTitle: '🤖 What is Lyon?',
      whatIsLyonBody: 'Lyon is the intelligent assistant inside DamyxIA. It guides you, explains, helps and automates tasks inside the Genie. It does not save, publish or manage your account.',
      howTogetherTitle: '🔗 How do they work together?',
      howTogetherLines: [
        'Lyon guides. DamyxIA executes.',
        'Lyon opens modules. DamyxIA loads them.',
        'Lyon suggests. DamyxIA saves.',
      ],
      whereWorkTitle: '🛠️ Where do I do my work?',
      whereWorkItems: [
        'Create App → DamyxIA',
        'Edit App → DamyxIA (Builder)',
        'Publish → DamyxIA',
        'Import template → DamyxIA (guided by Lyon)',
        'Ask questions → Lyon',
        'Generate content → Lyon',
      ],
      summaryTitle: 'Summary:',
      summaryLines: [
        'Lyon is your copilot.',
        'DamyxIA is your platform.',
      ],
      scrollTop: 'Back to top',
      closeAria: 'Close',
    },

    close: 'Close'
  }
};

// Collapsible Section Component
// 🟦 2026-06-27 — Mini-componente "icon" que renderiza el logo de DamyxIA
// con rotación lenta (mismo efecto que el rectángulo "Qué saber sobre
// DamyxIA"). Se pasa a la Section 1 ("Bienvenido a DamyxIA") en vez del
// ícono Sparkles para mantener la identidad visual de la marca.
const DamyxIALogoIcon = ({ className = '' }) => (
  <img
    src="/icons/icon-192x192.png"
    alt="DamyxIA"
    className={`${className} object-contain drop-shadow`}
    style={{ animation: 'spin 20s linear infinite' }}
  />
);

const Section = ({ icon: Icon, title, children, defaultOpen = false, color = 'blue', number }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    purple: 'from-purple-500 to-purple-600',
    emerald: 'from-emerald-500 to-emerald-600',
    amber: 'from-amber-500 to-amber-600',
    cyan: 'from-cyan-500 to-cyan-600',
    indigo: 'from-indigo-500 to-indigo-600',
    pink: 'from-pink-500 to-pink-600',
    teal: 'from-teal-500 to-teal-600'
  };
  
  return (
    <div className="mb-4 bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-700/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          {number && (
            <span className="w-7 h-7 flex items-center justify-center bg-slate-700 rounded-full text-sm font-bold text-cyan-400">
              {number}
            </span>
          )}
          <div className={`p-2 rounded-lg bg-gradient-to-br ${colorClasses[color]} shadow-lg`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-lg font-semibold text-white">{title}</h3>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>
      {isOpen && (
        <div className="px-4 pb-4 pt-2 border-t border-slate-700/50">
          {children}
        </div>
      )}
    </div>
  );
};

// Bullet List Component
const BulletList = ({ items, color = 'cyan' }) => (
  <ul className="space-y-1">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2 text-slate-300">
        <span className={`text-${color}-400 mt-0.5`}>•</span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

// Code Examples Component
const CodeExamples = ({ items }) => (
  <div className="flex flex-wrap gap-2 mt-2">
    {items.map((item, i) => (
      <code key={i} className="text-sm bg-slate-900 px-3 py-1.5 rounded-lg text-cyan-300 border border-slate-700">
        {item}
      </code>
    ))}
  </div>
);

// Mode Card Component
const ModeCard = ({ title, items, isWork = false }) => (
  <div className={`p-4 rounded-lg border ${isWork ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-blue-500/10 border-blue-500/30'}`}>
    <h5 className={`font-semibold mb-2 ${isWork ? 'text-emerald-400' : 'text-blue-400'}`}>{title}</h5>
    <ul className="space-y-1">
      {items.map((item, i) => (
        <li key={i} className="text-slate-300 text-sm flex items-start gap-2">
          <span className={isWork ? 'text-emerald-400' : 'text-blue-400'}>→</span>
          {item}
        </li>
      ))}
    </ul>
  </div>
);

// Behavior Item Component
const BehaviorItem = ({ text }) => {
  const [left, right] = text.split('→').map(s => s.trim());
  return (
    <div className="flex items-center gap-2 p-2 bg-slate-900/50 rounded-lg">
      <span className="text-slate-300 text-sm">{left}</span>
      <span className="text-cyan-400">→</span>
      <span className="text-cyan-300 text-sm font-medium">{right}</span>
    </div>
  );
};

const HomepageModal = ({ isOpen, onClose }) => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = translations[language] || translations.es;

  // 🟦 V12.7 — Estado del nuevo panel "Lyon IA — Su Alcance".
  const [showLyonAlcance, setShowLyonAlcance] = useState(false);
  // 🟦 V18.1 — Estado del modal "¿Qué es Ayuda con Lyon IA?"
  const [showLyonHelpInfo, setShowLyonHelpInfo] = useState(false);
  // 🟦 V18.3 — Modal "Reglas Oficiales — DamyxIA y Lyon-IA" (Cuadro Negro).
  const [showPlanesReglas, setShowPlanesReglas] = useState(false);
  // 🟦 CUADRO NEGRO 2026-02-23 — Modal "DamyxIA / Lyon — Funciones" (Home casita).
  const [showFuncionesPanel, setShowFuncionesPanel] = useState(false);
  // 🟥 2026-07-06 — Modal explicativo de Integración con GitHub (entre Recompensas y Panel del Creador).
  const [showGithubInfo, setShowGithubInfo] = useState(false);
  // 🟥 2026-07-06 — Botón dinámico "Convertir este Trabajo en App" (solo si hay módulos activos).
  const [activeWorkspaceModule, setActiveWorkspaceModule] = useState(null);
  const [convertingToApp, setConvertingToApp] = useState(false);
  const [convertError, setConvertError] = useState(null);

  // Fetch del último módulo activo del Workspace cuando se abre el modal.
  useEffect(() => {
    if (!showGithubInfo) return;
    let cancelled = false;
    (async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const res = await axios.get(`${API_URL}/api/damyxia/modules`);
        const list = Array.isArray(res.data) ? res.data : (res.data?.modules || []);
        if (cancelled) return;
        if (list.length > 0) {
          // Ordenar por updated_at desc y tomar el más reciente.
          const sorted = [...list].sort((a, b) => {
            const ta = new Date(a.updated_at || a.created_at || 0).getTime();
            const tb = new Date(b.updated_at || b.created_at || 0).getTime();
            return tb - ta;
          });
          setActiveWorkspaceModule(sorted[0]);
        } else {
          setActiveWorkspaceModule(null);
        }
      } catch (_) {
        if (!cancelled) setActiveWorkspaceModule(null);
      }
    })();
    return () => { cancelled = true; };
  }, [showGithubInfo]);

  // Handler: convertir el Trabajo activo en una App del Builder y abrir el Builder.
  const handleConvertToApp = async () => {
    if (!activeWorkspaceModule || convertingToApp) return;
    setConvertError(null);
    setConvertingToApp(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const mod = activeWorkspaceModule;
      const appName = (mod.name || 'Nueva App').trim().slice(0, 80);
      // POST /api/apps con query param `?name=...` para evitar preflight CORS.
      const res = await axios.post(
        `${API_URL}/api/apps?name=${encodeURIComponent(appName)}`,
      );
      const appId = res?.data?.id;
      if (!appId) throw new Error('No app id returned');

      // 🟥 2026-07-06 — Pre-cargar el contenido del Workspace en la App:
      //  1) README.md (config) — histórico / documentación.
      //  2) index.html (code) — visible en el canvas del Builder para que
      //     el usuario vea su contenido renderizado al abrir la app.
      try {
        const esc = (s) => String(s || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
        const nl2br = (s) => esc(s).replace(/\n/g, '<br />');

        const lines = [];
        lines.push(`# ${mod.name || appName}`);
        lines.push('');
        if (mod.base_type) {
          lines.push(`> ${language === 'es' ? 'Tipo' : 'Type'}: **${mod.base_type}**`);
          lines.push('');
        }
        lines.push(
          language === 'es'
            ? `_Importado desde el Workspace de DamyxIA el ${new Date().toLocaleString('es-AR')}._`
            : `_Imported from DamyxIA Workspace on ${new Date().toLocaleString('en-US')}._`
        );
        lines.push('');
        lines.push('---');
        lines.push('');
        lines.push(`## ${language === 'es' ? 'Descripción' : 'Description'}`);
        lines.push('');
        lines.push(mod.description && mod.description.trim() ? mod.description : (language === 'es' ? '_(Sin descripción)_' : '_(No description)_'));
        if (mod.code && String(mod.code).trim()) {
          lines.push('');
          lines.push('---');
          lines.push('');
          lines.push(`## ${language === 'es' ? 'Contenido del Trabajo' : 'Job content'}`);
          lines.push('');
          lines.push('```');
          lines.push(String(mod.code));
          lines.push('```');
        }
        const readme = lines.join('\n');

        // Construimos un index.html con el mismo contenido en HTML + Tailwind,
        // sustituyendo el placeholder del boilerplate para que el canvas del
        // Builder muestre inmediatamente el material del Workspace.
        const hasDesc = mod.description && String(mod.description).trim();
        const hasCode = mod.code && String(mod.code).trim();
        const html = [
          '<!DOCTYPE html>',
          '<html lang="' + (language === 'es' ? 'es' : 'en') + '">',
          '<head>',
          '  <meta charset="UTF-8" />',
          '  <meta name="viewport" content="width=device-width, initial-scale=1.0" />',
          `  <title>${esc(mod.name || appName)}</title>`,
          '  <script src="https://cdn.tailwindcss.com"></script>',
          '  <style>body { margin: 0; font-family: system-ui, -apple-system, sans-serif; background: #0f172a; color: #e2e8f0; min-height: 100vh; }</style>',
          '</head>',
          '<body>',
          '  <main class="max-w-3xl mx-auto px-6 py-12">',
          '    <div class="mb-8">',
          `      <h1 class="text-3xl md:text-4xl font-bold text-white leading-tight">${esc(mod.name || appName)}</h1>`,
          mod.base_type
            ? `      <div class="mt-3"><span class="inline-block px-3 py-1 text-xs font-medium rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300">${esc(mod.base_type)}</span></div>`
            : '',
          `      <p class="mt-3 text-xs text-slate-500">${esc(language === 'es' ? 'Importado desde el Workspace de DamyxIA el ' + new Date().toLocaleString('es-AR') : 'Imported from DamyxIA Workspace on ' + new Date().toLocaleString('en-US'))}</p>`,
          '    </div>',
          '    <section class="mb-8">',
          `      <h2 class="text-lg font-semibold text-white mb-2 border-b border-slate-700 pb-2">${esc(language === 'es' ? 'Descripción' : 'Description')}</h2>`,
          `      <div class="text-sm text-slate-300 leading-relaxed">${hasDesc ? nl2br(mod.description) : `<em class=\"text-slate-500\">${esc(language === 'es' ? '(Sin descripción)' : '(No description)')}</em>`}</div>`,
          '    </section>',
          hasCode ? [
            '    <section>',
            `      <h2 class="text-lg font-semibold text-white mb-2 border-b border-slate-700 pb-2">${esc(language === 'es' ? 'Contenido del Trabajo' : 'Job content')}</h2>`,
            '      <pre class="text-xs text-slate-200 bg-slate-900/80 border border-slate-700 rounded-lg p-4 overflow-auto"><code>' + esc(mod.code) + '</code></pre>',
            '    </section>',
          ].join('\n') : '',
          '  </main>',
          '</body>',
          '</html>',
        ].filter(Boolean).join('\n');

        await axios.post(`${API_URL}/api/apps/${appId}/files`, {
          files: [
            { path: 'README.md', content: readme, type: 'config' },
            { path: 'index.html', content: html, type: 'code' },
          ],
        });
      } catch (_) {
        // Safe-fail: si el pre-carga falla, seguimos abriendo el Builder igual.
      }

      setShowGithubInfo(false);
      onClose && onClose();
      // 🟥 CUADRO NEGRO MAESTRO (sec 2) — PROHIBIDO el auto-open del Builder.
      navigate('/my-apps');
    } catch (err) {
      setConvertError(
        language === 'es'
          ? 'No pudimos crear la App. Intentá nuevamente en unos segundos.'
          : 'We could not create the App. Please try again in a few seconds.'
      );
    } finally {
      setConvertingToApp(false);
    }
  };
  const funcionesPanelRef = useRef(null);
  
  // Obtener email del usuario para verificar si es el creador
  const userEmail = localStorage.getItem('userEmail') || '';
  const isCreator = ['damyxia@gmail.com', 'marcmarl2@gmail.com', 'manager@damyxia.com', 'support@damyxia.com'].includes(userEmail);
  
  const handleNavigateToPlans = () => {
    onClose();
    navigate('/planes-y-beneficios');
  };
  
  const handleNavigateToRewards = () => {
    onClose();
    navigate('/sistema-de-recompensas');
  };
  
  const handleNavigateToCreatorPanel = () => {
    onClose();
    navigate('/admin/creator');
  };

  const handleNavigateToCapabilities = () => {
    onClose();
    navigate('/capabilities');
  };
  
  if (!isOpen) return null;
  
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-stretch sm:items-center justify-center p-0 sm:p-4 overflow-y-auto"
      style={{
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}
    >
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/80"
        onClick={onClose}
      />
      
      {/* Modal — Flex column: header (auto) + content (flex:1 scroll) + footer (auto) */}
      <div
        className="relative w-full max-w-4xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 sm:rounded-2xl border border-slate-700/50 shadow-2xl flex flex-col"
        style={{
          // dvh respeta las barras del browser (URL/gesture) — fallback a vh.
          minHeight: '100dvh',
          maxHeight: '100dvh',
        }}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-indigo-900/90 via-purple-900/90 to-cyan-900/90 backdrop-blur-md border-b border-slate-700/50 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-white/10 rounded-xl">
                <Home className="w-6 h-6 text-cyan-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-cyan-400 via-purple-400 to-indigo-400 bg-clip-text text-transparent">
                  {t.title}
                </h1>
                <p className="text-sm text-slate-400">{t.subtitle}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-lg transition-colors"
              data-testid="close-homepage-modal"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
        
        {/* Content — flex:1 + overflowY auto: contenedor interno con scroll */}
        <div
          className="p-6"
          style={{
            flex: '1 1 auto',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          
          {/* Quick Summary - Always visible */}
          <div className="mb-6 p-6 bg-gradient-to-r from-amber-500/10 via-yellow-500/10 to-amber-500/10 rounded-xl border border-amber-500/30">
            <div className="flex items-center gap-3 mb-4">
              <Sparkles className="w-6 h-6 text-amber-400" />
              <h2 className="text-xl font-bold text-amber-400">{t.quickSummaryTitle}</h2>
            </div>
            <p className="text-slate-200 mb-4">{t.quickSummary}</p>
            <div className="space-y-2">
              {t.quickSummaryPoints.map((point, i) => (
                <p key={i} className="text-slate-300 pl-4 border-l-2 border-amber-500/50">{point}</p>
              ))}
            </div>
          </div>
          
          {/* Natural Interaction Section - NEW */}
          <div className="mb-6 p-6 bg-gradient-to-r from-green-500/10 via-emerald-500/10 to-green-500/10 rounded-xl border border-green-500/30">
            <div className="flex items-center gap-3 mb-4">
              <Home className="w-6 h-6 text-green-400" />
              <h2 className="text-xl font-bold text-green-400">{t.naturalInteractionTitle}</h2>
            </div>
            <p className="text-slate-200 mb-4">{t.naturalInteractionIntro}</p>
            <p className="text-slate-300 mb-3 font-medium">{t.naturalInteractionRecommendation}</p>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {t.naturalInteractionExamples.map((example, i) => (
                <div key={i} className="p-2 bg-green-500/10 rounded-lg border border-green-500/20">
                  <p className="text-green-300 text-sm text-center">{example}</p>
                </div>
              ))}
            </div>
            <p className="text-slate-400 text-sm mb-3">{t.naturalInteractionExplanation}</p>
            <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/30">
              <p className="text-green-300 text-center text-sm font-medium">{t.naturalInteractionSummary}</p>
            </div>
          </div>

          {/* Section 1: Welcome — 🟪 P24.3 · Guía de Capacidades completa
              en lugar del recuadro-highlight simple. Owner-approved layout
              con tarjetas navegables + scroll interno para CTAs. */}
          <Section icon={DamyxIALogoIcon} title={t.section1Title} defaultOpen={false} color="indigo" number="1">
            <p className="text-slate-300 mb-4">{t.section1Content}</p>
            <CapabilitiesGuide t={t.guide} />
          </Section>
          
          {/* Plans Block — 🟦 V18.3: abre el modal con el "Cuadro Negro
              Oficial" de reglas DamyxIA + Lyon-IA. El modal a su vez ofrece
              un botón "Ver planes completos →" que navega a la página. */}
          <button
            onClick={() => setShowPlanesReglas(true)}
            className="w-full mb-4 p-4 bg-gradient-to-r from-purple-500/10 via-pink-500/10 to-purple-500/10 rounded-xl border border-purple-500/30 hover:border-purple-400/50 hover:bg-purple-500/15 transition-all group"
            data-testid="plans-benefits-block"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 shadow-lg">
                  <Crown className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="text-lg font-semibold text-white">{t.plansBlockTitle}</h3>
                  <p className="text-sm text-slate-400">{t.plansBlockDesc}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-purple-400 group-hover:text-purple-300 transition-colors">
                <span className="text-sm font-medium">{t.plansBlockCta}</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </button>
          
          {/* Rewards System Block - Link to /sistema-de-recompensas */}
          <button
            onClick={handleNavigateToRewards}
            className="w-full mb-4 p-4 bg-gradient-to-r from-amber-500/10 via-yellow-500/10 to-amber-500/10 rounded-xl border border-amber-500/30 hover:border-amber-400/50 hover:bg-amber-500/15 transition-all group"
            data-testid="rewards-system-block"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-amber-500 to-yellow-500 shadow-lg">
                  <Gift className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="text-lg font-semibold text-white">{t.rewardsBlockTitle}</h3>
                  <p className="text-sm text-slate-400">{t.rewardsBlockDesc}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-amber-400 group-hover:text-amber-300 transition-colors">
                <span className="text-sm font-medium">{t.rewardsBlockCta}</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </button>

          {/* 🟥 GitHub Integration Block (2026-07-06) — entre Recompensas y Panel del Creador */}
          <button
            onClick={() => setShowGithubInfo(true)}
            className="w-full mb-4 p-4 bg-gradient-to-r from-slate-700/40 via-zinc-700/30 to-slate-700/40 rounded-xl border border-slate-500/40 hover:border-slate-400/60 hover:bg-slate-700/50 transition-all group"
            data-testid="github-integration-block"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-slate-800 to-zinc-900 shadow-lg border border-slate-600/40">
                  <Github className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="text-lg font-semibold text-white">{t.githubBlockTitle}</h3>
                  <p className="text-sm text-slate-400">{t.githubBlockDesc}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-slate-300 group-hover:text-white transition-colors">
                <span className="text-sm font-medium">{t.githubBlockCta}</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </button>
          
          {/* Creator Panel Block - Solo visible para damyxia@gmail.com (+ admins de respaldo) */}
          {isCreator && (
            <button
              onClick={handleNavigateToCreatorPanel}
              className="w-full mb-4 p-4 bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-amber-500/10 rounded-xl border border-amber-500/30 hover:border-amber-400/50 hover:bg-amber-500/15 transition-all group"
              data-testid="creator-panel-block"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 shadow-lg">
                    <Crown className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-lg font-semibold text-white">{t.creatorBlockTitle}</h3>
                    <p className="text-sm text-slate-400">{t.creatorBlockDesc}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-amber-400 group-hover:text-amber-300 transition-colors">
                  <span className="text-sm font-medium">{t.creatorBlockCta}</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </button>
          )}
          
          {/* About DamyxIA Block - Visible para todos. Navega internamente a /capabilities */}
          <button
            onClick={handleNavigateToCapabilities}
            className="w-full mb-4 p-8 bg-gradient-to-r from-[#2D1B69] via-indigo-900/60 to-[#2D1B69] rounded-xl border border-indigo-500/30 hover:border-indigo-400/50 hover:from-[#3D2179] hover:to-[#3D2179] transition-all group"
            data-testid="about-damyxia-block"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {/* 🟦 V10.5c — Glow violeta pulsante detrás del logo (PERMANENTE — confirmado por el creador 2026-06-16). */}
                <div className="relative w-12 h-12 flex-shrink-0">
                  <div
                    className="absolute inset-0 rounded-full bg-violet-500/40 blur-xl animate-pulse"
                    aria-hidden="true"
                    data-testid="about-damyxia-block-glow"
                  />
                  <img
                    src="/icons/icon-192x192.png"
                    alt="DamyxIA"
                    className="relative w-12 h-12 object-contain hover:scale-110 transition-transform duration-300 drop-shadow-lg"
                    style={{ animation: 'spin 20s linear infinite' }}
                    data-testid="about-damyxia-block-logo"
                  />
                </div>
                {/* END V10.5c */}
                <div className="text-left">
                  <h3 className="text-lg font-semibold text-white">{t.aboutBlockTitle}</h3>
                  <p className="text-sm text-slate-300">{t.aboutBlockDesc}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-indigo-300 group-hover:text-indigo-200 transition-colors">
                <span className="text-sm font-medium">{t.aboutBlockCta}</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </button>

          {/* 🟦 V12.7 — Lyon IA — Su Alcance (debajo del rectángulo "Que Saber sobre DamyxIA"). */}
          <button
            type="button"
            onClick={() => setShowLyonAlcance(true)}
            data-testid="home-lyon-alcance"
            className="w-full mb-4 p-6 rounded-xl border border-purple-500/30 hover:border-purple-400/60 transition-all group text-left"
            style={{
              background: 'linear-gradient(90deg, rgba(124,58,237,0.22), rgba(15,15,26,0.85) 60%, rgba(34,211,238,0.10))',
            }}
          >
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className="relative w-14 h-14 flex-shrink-0 rounded-full bg-purple-500/20 border border-purple-400/40 flex items-center justify-center overflow-visible"
                  data-testid="home-lyon-alcance-avatar"
                >
                  {/* 🟦 V18.2 — Geniecito oficial con burbujitas. */}
                  <LyonGenieIcon size={32} bubbles />
                  {/* Halo glow */}
                  <div className="absolute inset-0 rounded-full bg-purple-400/30 blur-md animate-pulse" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base sm:text-lg font-semibold text-white">
                    {language === 'es' ? 'Lyon IA — Su Alcance' : 'Lyon AI — Its Scope'}
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-300 truncate">
                    {language === 'es'
                      ? 'Todo lo que el Genio puede hacer dentro de DamyxIA.'
                      : 'Everything the Genie can do inside DamyxIA.'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-purple-200 group-hover:text-white transition-colors flex-shrink-0">
                <span className="text-sm font-medium hidden sm:inline">
                  {language === 'es' ? 'Abrir' : 'Open'}
                </span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </button>

          {/* 🟦 V18.1 — Card "¿Qué es Ayuda con Lyon IA?" (debajo de "Lyon IA — Su Alcance"). */}
          <button
            type="button"
            onClick={() => setShowLyonHelpInfo(true)}
            data-testid="home-lyon-help-info"
            className="w-full mb-4 p-6 rounded-xl border border-fuchsia-500/30 hover:border-fuchsia-400/60 transition-all group text-left"
            style={{
              background: 'linear-gradient(90deg, rgba(192,38,211,0.20), rgba(15,15,26,0.85) 60%, rgba(99,102,241,0.12))',
            }}
          >
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="relative w-10 h-10 flex-shrink-0 rounded-full bg-fuchsia-500/20 border border-fuchsia-400/40 flex items-center justify-center">
                  <HelpCircle className="w-5 h-5 text-fuchsia-200" />
                  <div className="absolute inset-0 rounded-full bg-fuchsia-400/30 blur-md animate-pulse" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base sm:text-lg font-semibold text-white">
                    {language === 'es' ? '¿Qué es Ayuda con Lyon IA?' : 'What is Lyon-IA Help?'}
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-300 truncate">
                    {language === 'es'
                      ? 'Guía completa: cómo hablar con Lyon-IA y qué pedirle, en Workspace y Builder.'
                      : 'Full guide: how to talk to Lyon-IA and what to ask for, in Workspace and Builder.'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-fuchsia-200 group-hover:text-white transition-colors flex-shrink-0">
                <span className="text-sm font-medium hidden sm:inline">
                  {language === 'es' ? 'Abrir' : 'Open'}
                </span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </button>

          {/* 🟦 CUADRO NEGRO 2026-06-27 — Botón "DamyxIA / Lyon — Funciones"
              REUBICADO por orden del creador: debajo del card "¿Qué es Ayuda
              con Lyon IA?" y encima de "El Área de Trabajo (Workspace)". */}
          <button
            type="button"
            onClick={() => setShowFuncionesPanel(true)}
            data-testid="btn-damyxia-lyon-funciones"
            className="w-full px-4 py-3 mb-3 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-semibold text-sm shadow-lg shadow-indigo-500/25 transition-all flex items-center justify-center gap-2"
            title={language === 'es' ? 'DamyxIA / Lyon — Funciones' : 'DamyxIA / Lyon — Features'}
          >
            <Sparkles className="w-4 h-4" />
            {language === 'es' ? 'DamyxIA / Lyon — Funciones' : 'DamyxIA / Lyon — Features'}
          </button>

          {/* Section 2: Workspace */}
          <Section icon={Layout} title={t.section2Title} color="purple" number="2">
            <p className="text-slate-300 mb-4">{t.section2Intro}</p>
            
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="p-4 bg-slate-900/50 rounded-lg border border-slate-700/30">
                <h4 className="text-sm font-semibold text-purple-400 mb-3">{t.section2WhatTitle}</h4>
                <div className="flex flex-wrap gap-2">
                  {t.section2WhatItems.map((item, i) => (
                    <span key={i} className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm">{item}</span>
                  ))}
                </div>
              </div>
              <div className="p-4 bg-slate-900/50 rounded-lg border border-slate-700/30">
                <h4 className="text-sm font-semibold text-purple-400 mb-3">{t.section2HowTitle}</h4>
                <BulletList items={t.section2HowItems} color="purple" />
              </div>
            </div>
            
            <div className="p-4 bg-purple-500/10 rounded-lg border border-purple-500/30">
              <p className="text-purple-300 text-center font-medium">{t.section2Highlight}</p>
            </div>
          </Section>
          
          {/* Section 3: Vision Replication */}
          <Section icon={Image} title={t.section3Title} color="cyan" number="3">
            <p className="text-slate-300 mb-6">{t.section3Intro}</p>
            
            {/* 3.1 Analysis */}
            <div className="mb-6 p-4 bg-blue-500/5 rounded-lg border border-blue-500/20">
              <h4 className="text-md font-semibold text-blue-400 mb-3 flex items-center gap-2">
                <Eye className="w-4 h-4" />
                {t.section3AnalysisTitle}
              </h4>
              <p className="text-slate-400 text-sm mb-3">{t.section3AnalysisIntro}</p>
              <div className="flex flex-wrap gap-2 mb-4">
                {t.section3AnalysisCapabilities.map((cap, i) => (
                  <span key={i} className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-sm">{cap}</span>
                ))}
              </div>
              <p className="text-slate-400 text-sm mb-2">{t.section3AnalysisMode}</p>
              <BulletList items={t.section3AnalysisModePoints} color="blue" />
              <div className="mt-3 pt-3 border-t border-slate-700/50">
                <p className="text-xs text-slate-500 mb-2">Ejemplos:</p>
                <CodeExamples items={t.section3AnalysisExamples} />
              </div>
            </div>
            
            {/* 3.2 Replication */}
            <div className="mb-6 p-4 bg-emerald-500/5 rounded-lg border border-emerald-500/20">
              <h4 className="text-md font-semibold text-emerald-400 mb-3 flex items-center gap-2">
                <Copy className="w-4 h-4" />
                {t.section3ReplicationTitle}
              </h4>
              <p className="text-slate-400 text-sm mb-2">{t.section3ReplicationIntro}</p>
              <CodeExamples items={t.section3ReplicationExamples} />
              <p className="text-emerald-300 text-sm mt-3 p-2 bg-emerald-500/10 rounded">{t.section3ReplicationResult}</p>
            </div>
            
            {/* 3.3 Editing */}
            <div className="mb-6 p-4 bg-pink-500/5 rounded-lg border border-pink-500/20">
              <h4 className="text-md font-semibold text-pink-400 mb-3 flex items-center gap-2">
                <Palette className="w-4 h-4" />
                {t.section3EditTitle}
              </h4>
              <p className="text-slate-400 text-sm mb-2">{t.section3EditIntro}</p>
              <CodeExamples items={t.section3EditExamples} />
              <p className="text-pink-300 text-sm mt-3 p-2 bg-pink-500/10 rounded">{t.section3EditResult}</p>
            </div>
            
            {/* 3.4 Mode Activation */}
            <div className="mb-4">
              <h4 className="text-md font-semibold text-white mb-4">{t.section3ModesTitle}</h4>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <ModeCard title={t.section3AnalysisModeTitle} items={t.section3AnalysisModeDesc} />
                <ModeCard title={t.section3WorkModeTitle} items={t.section3WorkModeDesc} isWork={true} />
              </div>
              <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
                <p className="text-amber-400 text-sm font-medium mb-2">{t.section3Keywords}</p>
                <p className="text-amber-300/80 text-sm italic">{t.section3KeywordsList}</p>
              </div>
            </div>
          </Section>
          
          {/* Section 4: Upload Images (NEW) */}
          <Section icon={Upload} title={t.section4Title} color="teal" number="4">
            <p className="text-slate-300 mb-4">{t.section4Intro}</p>
            
            {/* How it works */}
            <div className="mb-4 p-4 bg-slate-900/50 rounded-lg border border-slate-700/30">
              <h4 className="text-sm font-semibold text-teal-400 mb-3">{t.section4HowTitle}</h4>
              <BulletList items={t.section4HowItems} color="teal" />
            </div>
            
            {/* Behavior */}
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-teal-400 mb-3">{t.section4BehaviorTitle}</h4>
              <div className="grid grid-cols-2 gap-2">
                {t.section4BehaviorItems.map((item, i) => (
                  <BehaviorItem key={i} text={item} />
                ))}
              </div>
            </div>
            
            {/* Tests */}
            <div className="p-4 bg-teal-500/10 rounded-lg border border-teal-500/30">
              <h4 className="text-sm font-semibold text-teal-400 mb-3 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                {t.section4TestsTitle}
              </h4>
              <div className="grid grid-cols-2 gap-2">
                {t.section4TestsItems.map((test, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <span className="text-emerald-400">✓</span>
                    <span className="text-slate-300">{test}</span>
                  </div>
                ))}
              </div>
            </div>
          </Section>

          {/* Section 4.7: Cómo funciona la Papelera (Spec oficial Free + Premium) */}
          <Section icon={FileText} title={t.sectionTrashTitle} color="amber" number="4.7">
            <p className="text-slate-300 text-sm mb-3 italic">{t.sectionTrashSubtitle}</p>
            <ul className="space-y-2 text-sm text-slate-300">
              {t.sectionTrashSteps.map((step, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-amber-400 font-semibold shrink-0">{i + 1}.</span>
                  <span>{step}</span>
                </li>
              ))}
            </ul>
          </Section>

          {/* Section 4.8: Limites de texto en el cuadro de dialogo (FAQ) */}
          <Section icon={Info} title={t.sectionTextLimitsTitle} color="emerald" number="4.8">
            <div
              id="faq-text-limits"
              data-testid="faq-text-limits"
              className="space-y-4"
            >
              <p className="text-slate-300">
                {t.sectionTextLimitsIntro}{' '}
                <strong className="text-emerald-300">{t.sectionTextLimitsHighlight}</strong>
              </p>
              <p className="text-slate-300">
                {t.sectionTextLimitsErrorIntro}{' '}
                <span className="text-red-400">{t.sectionTextLimitsErrorMsg}</span>
              </p>
              <p className="text-slate-300">{t.sectionTextLimitsTotal}</p>
              <p className="text-slate-300">{t.sectionTextLimitsTip}</p>
            </div>
          </Section>

          {/* Section 5: Create and Organize */}
          <Section icon={FileText} title={t.section5Title} color="amber" number="5">
            {/* 5.1 Create */}
            <div className="mb-4">
              <h4 className="text-md font-semibold text-amber-400 mb-2">{t.section5CreateTitle}</h4>
              <p className="text-slate-400 text-sm mb-2">{t.section5CreateIntro}</p>
              <CodeExamples items={t.section5CreateExamples} />
              <p className="text-slate-300 text-sm mt-3">{t.section5CreateResult}</p>
            </div>
            
            {/* 5.2 Update */}
            <div className="mb-4">
              <h4 className="text-md font-semibold text-amber-400 mb-2">{t.section5UpdateTitle}</h4>
              <p className="text-slate-400 text-sm mb-2">{t.section5UpdateIntro}</p>
              <CodeExamples items={t.section5UpdateExamples} />
            </div>
            
            {/* 5.3 Organization */}
            <div className="p-4 bg-amber-500/10 rounded-lg border border-amber-500/30">
              <h4 className="text-md font-semibold text-amber-400 mb-2">{t.section5OrgTitle}</h4>
              <p className="text-slate-400 text-sm mb-2">{t.section5OrgIntro}</p>
              <BulletList items={t.section5OrgItems} color="amber" />
            </div>
          </Section>
          
          {/* Section 6: History */}
          <Section icon={History} title={t.section6Title} color="emerald" number="6">
            <p className="text-slate-300 mb-4">{t.section6Content}</p>
            <BulletList items={t.section6Points} color="emerald" />
            <div className="mt-4 p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
              <p className="text-emerald-300 text-center font-medium">{t.section6Highlight}</p>
            </div>
          </Section>
          
          {/* Section 7: Best Practices */}
          <Section icon={Lightbulb} title={t.section7Title} color="blue" number="7">
            <div className="grid md:grid-cols-2 gap-3">
              {t.section7Items.map((item, i) => (
                <div key={i} className="flex items-start gap-2 p-3 bg-slate-900/50 rounded-lg border border-slate-700/30">
                  <CheckCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-300 text-sm">{item}</span>
                </div>
              ))}
            </div>
          </Section>
          
          {/* Section 8: Final Message */}
          <Section icon={Sparkles} title={t.section8Title} defaultOpen={false} color="pink" number="8">
            <div className="space-y-3">
              {t.section8Lines.map((line, i) => (
                <p key={i} className="text-slate-300 text-center">{line}</p>
              ))}
            </div>
            <div className="mt-6 p-4 bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-pink-500/20 rounded-lg border border-purple-500/30">
              <p className="text-white text-center font-bold text-lg">{t.section8Final}</p>
            </div>
          </Section>
          
        </div>
        
        {/* Footer — siempre visible al final del flex column. Sin position:fixed.
            🟥 CUADRO NEGRO 2026-02-26 — En mobile añadimos paddingBottom extra
            (64px de la tab bar inferior + safe-area) para que NO se superponga
            con la barra Chat/Trabajo/Papelera/Historial. En desktop sólo usa
            safe-area-inset-bottom. */}
        <div
          className="bg-slate-900/95 border-t border-slate-700/50 px-6 py-4 homepage-modal-footer-safe"
          style={{
            flexShrink: 0,
          }}
        >
          {/* 🟥 CUADRO NEGRO 2026-02-26 — Enlaces "Términos · Privacidad"
              SOLO en MÓVIL (sm:hidden). En desktop quedan en el footer
              global. En móvil van aquí; añadimos `mb-16` para NO superponernos
              con la barra inferior fija (Chat/Trabajo/Papelera/Historial,
              que mide 64px ≈ pb-16). */}
          <div
            className="sm:hidden flex items-center justify-center gap-2 text-[12px] text-slate-400 mb-3"
            data-testid="homepage-legal-links-mobile"
          >
            <Link to="/terminos" data-testid="homepage-link-terminos" className="hover:text-white underline">
              {language === 'en' ? 'Terms' : 'Términos'}
            </Link>
            <span>·</span>
            <Link to="/privacidad" data-testid="homepage-link-privacidad" className="hover:text-white underline">
              {language === 'en' ? 'Privacy' : 'Privacidad'}
            </Link>
          </div>
          <button
            onClick={onClose}
            className="w-full py-3 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-400 hover:to-purple-400 text-white font-semibold rounded-xl transition-all shadow-lg hover:shadow-indigo-500/25"
            data-testid="close-homepage-btn"
          >
            {t.close}
          </button>
        </div>
        {/* 🟦 V12.7 — Panel "Lyon IA — Su Alcance" (modal sobre este modal). */}
        <LyonAlcancePanel
          isOpen={showLyonAlcance}
          onClose={() => setShowLyonAlcance(false)}
        />
        {/* 🟦 V18.1 — Modal "¿Qué es Ayuda con Lyon IA?" */}
        <LyonHelpInfoModal
          isOpen={showLyonHelpInfo}
          onClose={() => setShowLyonHelpInfo(false)}
        />
        {/* 🟦 V18.3 — Modal "Reglas Oficiales — DamyxIA y Lyon-IA" */}
        <PlanesReglasModal
          isOpen={showPlanesReglas}
          onClose={() => setShowPlanesReglas(false)}
        />

        {/* 🟥 2026-07-06 — Modal "Integración de Proyectos a GitHub" (guía Workspace → Builder → GitHub). */}
        {showGithubInfo && createPortal(
          <div
            className="fixed inset-0 z-[10001] flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
            data-testid="github-integration-modal-overlay"
            onClick={(e) => { if (e.target === e.currentTarget) setShowGithubInfo(false); }}
          >
            <div
              data-testid="github-integration-modal"
              className="relative w-full max-w-[720px] max-h-[88vh] overflow-y-auto rounded-2xl shadow-2xl border border-slate-600/50"
              style={{ background: '#0f0f1a', color: '#e5e7eb', padding: '28px' }}
            >
              <button
                onClick={() => setShowGithubInfo(false)}
                className="absolute top-4 right-4 p-2 rounded-lg hover:bg-white/10 transition-colors"
                data-testid="github-integration-modal-close"
                aria-label={language === 'es' ? 'Cerrar' : 'Close'}
              >
                <X className="w-5 h-5 text-slate-300" />
              </button>

              <div className="flex items-center gap-3 mb-5">
                <div className="p-2.5 rounded-lg bg-gradient-to-br from-slate-800 to-zinc-900 border border-slate-600/40 shadow-lg">
                  <Github className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">
                    {language === 'es' ? 'Integración de Proyectos a GitHub' : 'GitHub Project Integration'}
                  </h2>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {language === 'es'
                      ? 'Guía oficial: cómo convertir un Trabajo del Workspace en una App exportable a GitHub.'
                      : 'Official guide: how to convert a Workspace Job into an App exportable to GitHub.'}
                  </p>
                </div>
              </div>

              {language === 'es' ? (
                <div className="space-y-4 text-sm leading-relaxed">
                  <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                    <p className="text-amber-200 font-semibold">
                      La integración con GitHub solo existe desde el Builder, no en el Workspace.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-white font-semibold mb-2">¿Por qué?</h3>
                    <p className="text-slate-300">Porque el Workspace es:</p>
                    <ul className="list-disc list-inside text-slate-300 mt-1 space-y-0.5">
                      <li>un área de trabajo interna</li>
                      <li>donde creás ideas, textos, diseños, estructuras</li>
                      <li>pero NO genera código ni archivos</li>
                      <li>por lo tanto no tiene nada que subir a GitHub</li>
                    </ul>
                    <p className="text-slate-300 mt-2">GitHub solo acepta:</p>
                    <ul className="list-disc list-inside text-slate-300 mt-1 space-y-0.5">
                      <li>archivos</li>
                      <li>carpetas</li>
                      <li>proyectos</li>
                      <li>repositorios</li>
                      <li>código</li>
                      <li>assets</li>
                    </ul>
                    <p className="text-slate-300 mt-2">El Workspace genera contenido, no proyectos.</p>
                  </div>

                  <div>
                    <h3 className="text-white font-semibold mb-2">¿Cómo se guarda en GitHub?</h3>
                    <p className="text-slate-300">
                      Solo existe una forma:
                      <span className="text-amber-300 font-medium"> convertir tu Trabajo del Workspace en una App del Builder</span>.
                    </p>
                    <p className="text-slate-300 mt-2">Y recién ahí:</p>
                    <ul className="list-disc list-inside text-slate-300 mt-1 space-y-0.5">
                      <li>aparece el botón <span className="text-white font-medium">“Save to GitHub”</span></li>
                      <li>podés subir el proyecto completo</li>
                      <li>podés hacer commits</li>
                      <li>podés crear repositorios</li>
                      <li>podés actualizar código</li>
                    </ul>
                  </div>

                  <div className="p-4 rounded-lg bg-slate-800/60 border border-slate-600/40">
                    <h3 className="text-white font-semibold mb-3">Pasos exactos</h3>
                    <ol className="list-decimal list-inside space-y-2 text-slate-200">
                      <li>Abrí tu Trabajo en el Workspace (como el que tenés en los Dashboards).</li>
                      <li>Tocá el botón <span className="text-white font-medium">“+ Crear mi primera app”</span> (en Mis Apps).</li>
                      <li>
                        Creá una App vacía.
                        <div className="mt-1 ml-5 text-slate-400 text-xs">
                          Ejemplo — Nombre: <code className="px-1 py-0.5 bg-slate-900 rounded text-slate-100">Skay Medio Ambiente</code>
                        </div>
                      </li>
                      <li>Abrí esa App en el Builder con el botón <span className="text-white font-medium">“Abrir en el Builder”</span>.</li>
                      <li>
                        Importá tu contenido del Workspace. Tenés dos opciones:
                        <ul className="list-disc list-inside ml-5 mt-1 space-y-0.5 text-slate-300">
                          <li><span className="text-white font-medium">Opción A — Usar el Genio:</span> convierte tu texto en componentes.</li>
                          <li><span className="text-white font-medium">Opción B — Crear la pantalla manualmente:</span> agregar título, secciones, cuadros, textos, imágenes.</li>
                        </ul>
                      </li>
                      <li>
                        Cuando la App ya tenga contenido, aparecerá el botón <span className="text-white font-medium">“Save to GitHub”</span>.
                      </li>
                      <li>Presionalo — se conecta a tu cuenta, crea el repo, sube el proyecto, hace commits y guarda todo en GitHub.</li>
                    </ol>
                  </div>

                  <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                    <h3 className="text-emerald-200 font-semibold mb-2">Convertir un Trabajo del Workspace en una App exportable a GitHub</h3>
                    <ol className="list-decimal list-inside space-y-1.5 text-slate-200 text-[13px]">
                      <li>Abrir el Trabajo en el Workspace (por ejemplo “Skay – Consciente del Medio Ambiente”).</li>
                      <li>Ir a <span className="text-white font-medium">Mis Apps</span> en el menú principal de DamyxIA.</li>
                      <li>Crear una nueva App — Nombre: “Skay Medio Ambiente”, Estado: Borrador.</li>
                      <li>Abrir la App en el Builder con <span className="text-white font-medium">“Abrir en el Builder”</span>.</li>
                      <li>Dentro del Builder, recrear el contenido del Workspace: crear una página nueva, agregar título, cuadros de información (Biodiversidad, Cambio Climático, etc.), copiar y pegar textos del Workspace, agregar imágenes.</li>
                      <li>Guardar la App dentro del Builder.</li>
                      <li>Cuando la App tenga contenido, aparecerá el botón <span className="text-white font-medium">“Guardar en GitHub”</span> / <span className="text-white font-medium">“Save to GitHub”</span>.</li>
                      <li>Presionar el botón: seleccionar el repositorio, confirmar el commit, guardar el proyecto en GitHub.</li>
                      <li>Verificar en GitHub que el repositorio se creó correctamente.</li>
                    </ol>
                  </div>

                  <div>
                    <h3 className="text-white font-semibold mb-2">¿Por qué es así?</h3>
                    <ul className="list-disc list-inside text-slate-300 space-y-0.5">
                      <li>El Workspace NO genera código.</li>
                      <li>GitHub solo acepta archivos y proyectos.</li>
                      <li>El Builder sí genera código real.</li>
                      <li>Por eso el botón “Save to GitHub” solo aparece en el Builder.</li>
                    </ul>
                  </div>

                  <div className="p-3 rounded-lg bg-slate-800/60 border border-slate-600/40 text-slate-200">
                    <p className="mb-1">Tu trabajo del Workspace <span className="text-emerald-300 font-semibold">no está perdido</span>.</p>
                    <p className="mb-1">Tu trabajo <span className="text-emerald-300 font-semibold">sí se puede guardar en GitHub</span>.</p>
                    <p>Solo tenés que convertirlo en una App del Builder — después de eso, el botón aparece automáticamente.</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4 text-sm leading-relaxed">
                  <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                    <p className="text-amber-200 font-semibold">
                      GitHub integration only exists inside the Builder, not in the Workspace.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-white font-semibold mb-2">Why?</h3>
                    <p className="text-slate-300">Because the Workspace is:</p>
                    <ul className="list-disc list-inside text-slate-300 mt-1 space-y-0.5">
                      <li>an internal work area</li>
                      <li>where you create ideas, texts, designs, structures</li>
                      <li>but it does NOT generate code or files</li>
                      <li>so there is nothing to upload to GitHub</li>
                    </ul>
                    <p className="text-slate-300 mt-2">GitHub only accepts:</p>
                    <ul className="list-disc list-inside text-slate-300 mt-1 space-y-0.5">
                      <li>files</li>
                      <li>folders</li>
                      <li>projects</li>
                      <li>repositories</li>
                      <li>code</li>
                      <li>assets</li>
                    </ul>
                    <p className="text-slate-300 mt-2">The Workspace generates content, not projects.</p>
                  </div>

                  <div>
                    <h3 className="text-white font-semibold mb-2">How do you save to GitHub?</h3>
                    <p className="text-slate-300">
                      There is only one way:
                      <span className="text-amber-300 font-medium"> convert your Workspace Job into a Builder App</span>.
                    </p>
                    <p className="text-slate-300 mt-2">Then and only then:</p>
                    <ul className="list-disc list-inside text-slate-300 mt-1 space-y-0.5">
                      <li>the <span className="text-white font-medium">“Save to GitHub”</span> button appears</li>
                      <li>you can upload the whole project</li>
                      <li>you can make commits</li>
                      <li>you can create repositories</li>
                      <li>you can update code</li>
                    </ul>
                  </div>

                  <div className="p-4 rounded-lg bg-slate-800/60 border border-slate-600/40">
                    <h3 className="text-white font-semibold mb-3">Exact steps</h3>
                    <ol className="list-decimal list-inside space-y-2 text-slate-200">
                      <li>Open your Job in the Workspace (like the one you have in the Dashboards).</li>
                      <li>Click the <span className="text-white font-medium">“+ Create my first app”</span> button (in My Apps).</li>
                      <li>Create an empty App — Example name: <code className="px-1 py-0.5 bg-slate-900 rounded text-slate-100">Skay Environment</code>.</li>
                      <li>Open that App in the Builder using the <span className="text-white font-medium">“Open in Builder”</span> button.</li>
                      <li>
                        Import your Workspace content. Two options:
                        <ul className="list-disc list-inside ml-5 mt-1 space-y-0.5 text-slate-300">
                          <li><span className="text-white font-medium">Option A — Use the Genie:</span> it converts your text into components.</li>
                          <li><span className="text-white font-medium">Option B — Build the screen manually:</span> add title, sections, cards, texts, images.</li>
                        </ul>
                      </li>
                      <li>Once the App has content, the <span className="text-white font-medium">“Save to GitHub”</span> button will appear.</li>
                      <li>Click it — it connects to your account, creates the repo, uploads the project, makes commits and saves everything to GitHub.</li>
                    </ol>
                  </div>

                  <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                    <h3 className="text-emerald-200 font-semibold mb-2">Convert a Workspace Job into a GitHub-exportable App</h3>
                    <ol className="list-decimal list-inside space-y-1.5 text-slate-200 text-[13px]">
                      <li>Open the Job in the Workspace (for example “Skay – Environmentally Conscious”).</li>
                      <li>Go to <span className="text-white font-medium">My Apps</span> in the DamyxIA main menu.</li>
                      <li>Create a new App — Name: “Skay Environment”, Status: Draft.</li>
                      <li>Open the App in the Builder with <span className="text-white font-medium">“Open in Builder”</span>.</li>
                      <li>Inside the Builder, recreate the Workspace content: new page, title, information cards (Biodiversity, Climate Change, etc.), copy-paste Workspace texts, add images.</li>
                      <li>Save the App inside the Builder.</li>
                      <li>Once the App has content, the <span className="text-white font-medium">“Save to GitHub”</span> button will appear.</li>
                      <li>Click it: select the repository, confirm the commit, save the project to GitHub.</li>
                      <li>Verify on GitHub that the repository was created correctly.</li>
                    </ol>
                  </div>

                  <div>
                    <h3 className="text-white font-semibold mb-2">Why is it like this?</h3>
                    <ul className="list-disc list-inside text-slate-300 space-y-0.5">
                      <li>The Workspace does NOT generate code.</li>
                      <li>GitHub only accepts files and projects.</li>
                      <li>The Builder does generate real code.</li>
                      <li>That is why the “Save to GitHub” button only appears in the Builder.</li>
                    </ul>
                  </div>

                  <div className="p-3 rounded-lg bg-slate-800/60 border border-slate-600/40 text-slate-200">
                    <p className="mb-1">Your Workspace work is <span className="text-emerald-300 font-semibold">not lost</span>.</p>
                    <p className="mb-1">Your work <span className="text-emerald-300 font-semibold">can be saved to GitHub</span>.</p>
                    <p>You just need to convert it into a Builder App — after that, the button appears automatically.</p>
                  </div>
                </div>
              )}

              {/* 🟥 2026-07-06 — CTA dinámico: aparece SOLO si el usuario tiene un Trabajo activo en el Workspace. */}
              {activeWorkspaceModule && (
                <div
                  className="mt-6 p-4 rounded-xl border border-emerald-500/40"
                  style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.12), rgba(5,150,105,0.08))' }}
                  data-testid="github-convert-cta"
                >
                  <p className="text-xs text-emerald-300/80 mb-1">
                    {language === 'es' ? 'Trabajo activo detectado en tu Workspace' : 'Active Workspace job detected'}
                  </p>
                  <p className="text-white font-semibold text-base mb-3 truncate" title={activeWorkspaceModule.name}>
                    {activeWorkspaceModule.name}
                  </p>
                  <button
                    type="button"
                    onClick={handleConvertToApp}
                    disabled={convertingToApp}
                    className="w-full px-4 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-sm transition-colors flex items-center justify-center gap-2"
                    data-testid="github-convert-to-app-btn"
                  >
                    {convertingToApp ? (
                      <>
                        <span className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                        {language === 'es' ? 'Creando App…' : 'Creating App…'}
                      </>
                    ) : (
                      <>
                        <ArrowRight className="w-4 h-4" />
                        {language === 'es' ? 'Convertir este Trabajo en App' : 'Convert this Job into an App'}
                      </>
                    )}
                  </button>
                  <p className="text-[11px] text-slate-400 mt-2 leading-relaxed">
                    {language === 'es'
                      ? 'Se crea una App con el mismo nombre y se abre en el Builder. El contenido del Trabajo se pre-carga como página renderizada (index.html) y documentación (README.md) — vas a ver tu material en el canvas al instante. Después usá el botón “Save to GitHub”.'
                      : 'An App with the same name is created and opened in the Builder. Your Job content is pre-loaded as a rendered page (index.html) and documentation (README.md) — you will see your material in the canvas immediately. Then use the “Save to GitHub” button.'}
                  </p>
                  {convertError && (
                    <p className="text-xs text-red-300 mt-2" data-testid="github-convert-error">{convertError}</p>
                  )}
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}

        {/* 🟦 CUADRO NEGRO 2026-02-23 — Panel "DamyxIA / Lyon — Funciones" */}
        {showFuncionesPanel && (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
            data-testid="panel-damyxia-lyon-funciones-overlay"
            onClick={(e) => { if (e.target === e.currentTarget) setShowFuncionesPanel(false); }}
          >
            <div
              ref={funcionesPanelRef}
              data-testid="panel-damyxia-lyon-funciones"
              className="relative w-full max-w-[680px] max-h-[85vh] overflow-y-auto rounded-2xl shadow-2xl"
              style={{ background: '#0f0f1a', color: '#ffffff', padding: '24px', scrollBehavior: 'smooth' }}
            >
              <button
                type="button"
                onClick={() => setShowFuncionesPanel(false)}
                data-testid="btn-close-funciones"
                aria-label={t.funcionesPanel.closeAria}
                className="absolute top-3 right-3 p-2 rounded-lg hover:bg-white/10 transition-colors"
                style={{ color: '#ffffff' }}
              >
                <X className="w-5 h-5" />
              </button>

              <h2 className="text-2xl font-bold mb-6 pr-10">{t.funcionesPanel.title}</h2>

              <section className="mb-6">
                <h3 className="text-lg font-semibold mb-2">{t.funcionesPanel.whatIsDamyxiaTitle}</h3>
                <p className="text-slate-200 leading-relaxed">
                  {t.funcionesPanel.whatIsDamyxiaBody}
                </p>
              </section>

              <section className="mb-6">
                <h3 className="text-lg font-semibold mb-2">{t.funcionesPanel.whatIsLyonTitle}</h3>
                <p className="text-slate-200 leading-relaxed">
                  {t.funcionesPanel.whatIsLyonBody}
                </p>
              </section>

              <section className="mb-6">
                <h3 className="text-lg font-semibold mb-2">{t.funcionesPanel.howTogetherTitle}</h3>
                <p className="text-slate-200 leading-relaxed">
                  {t.funcionesPanel.howTogetherLines.map((line, i) => (
                    <React.Fragment key={i}>
                      {line}
                      {i < t.funcionesPanel.howTogetherLines.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </p>
              </section>

              <section className="mb-6">
                <h3 className="text-lg font-semibold mb-2">{t.funcionesPanel.whereWorkTitle}</h3>
                <ul className="text-slate-200 leading-relaxed space-y-1 list-none pl-0">
                  {t.funcionesPanel.whereWorkItems.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </section>

              <section className="mb-6 p-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <h3 className="text-lg font-semibold mb-2">{t.funcionesPanel.summaryTitle}</h3>
                <p className="text-slate-100 leading-relaxed">
                  {t.funcionesPanel.summaryLines.map((line, i) => (
                    <React.Fragment key={i}>
                      {line}
                      {i < t.funcionesPanel.summaryLines.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </p>
              </section>

              <button
                type="button"
                onClick={() => { funcionesPanelRef.current?.scrollTo({ top: 0, behavior: 'smooth' }); }}
                data-testid="btn-scroll-top"
                className="w-full mt-2 px-4 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-sm transition-colors"
              >
                {t.funcionesPanel.scrollTop}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default HomepageModal;               